import React from "react";
import axios from "axios";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Copy, Check, Image as ImageIcon, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard as copyText } from "../../utils/clipboard";
import RegenerateSection from "./RegenerateSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function TwitterTab({ content, onRegenerate, isRegenerating, onRegenerateItem, regeneratingItem }) {
  const [copied, setCopied] = React.useState(false);
  const [tweetImages, setTweetImages] = React.useState({});
  const [generatingImage, setGeneratingImage] = React.useState(null);

  const copyToClipboard = async (text) => {
    const success = await copyText(text);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const generateImage = async (tweet, index) => {
    setGeneratingImage(index);
    try {
      const prompt = `Create an engaging Twitter post image for: ${tweet.substring(0, 200)}`;
      
      const response = await axios.post(`${API}/images/generate`, {
        prompt,
        content_type: "twitter"
      });

      setTweetImages(prev => ({
        ...prev,
        [index]: response.data.image_url
      }));

      toast.success("Image generated!");
    } catch (error) {
      console.error("Image generation error:", error);
      toast.error("Failed to generate image");
    } finally {
      setGeneratingImage(null);
    }
  };

  const copyThread = () => {
    copyToClipboard(content.join("\n\n"));
  };

  if (!content || content.length === 0) {
    return (
      <div className="text-center text-white/60 py-12">
        <p className="mb-4">No Twitter content generated yet.</p>
        {onRegenerate && <RegenerateSection onRegenerate={onRegenerate} isRegenerating={isRegenerating} contentType="Twitter" />}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-bold text-white">Twitter Thread</h2>
        <p className="text-white/60">{content.length} tweets ready to post</p>
      </div>

      {/* Tweets */}
      <div className="space-y-4">
        {content.map((tweet, index) => {
          const charCount = tweet.length;
          return (
            <Card key={index} className="card">
              <CardContent className="pt-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00d4aa] to-[#00a885] flex items-center justify-center font-bold text-black">
                      EM
                    </div>
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-white">Epsillon Media</span>
                      <span className="text-white/40">@epsillonmedia</span>
                      <span className="text-white/40">· Tweet {index + 1}</span>
                    </div>
                    
                    {/* Generated Image */}
                    {tweetImages[index] && (
                      <div className="rounded-lg overflow-hidden">
                        <img 
                          src={tweetImages[index]} 
                          alt={`Tweet ${index + 1}`}
                          className="w-full h-48 object-cover"
                        />
                      </div>
                    )}
                    
                    <p className="text-white text-lg whitespace-pre-wrap">{tweet}</p>
                    
                    <Button
                      size="sm"
                      onClick={() => generateImage(tweet, index)}
                      disabled={generatingImage === index}
                      variant="outline"
                      className="w-full border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                    >
                      {generatingImage === index ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <ImageIcon className="w-3 h-3 mr-1" />
                          Generate Image
                        </>
                      )}
                    </Button>
                    
                    <div className="flex items-center justify-between pt-3 border-t border-white/10">
                      <div className="font-mono text-sm text-white/40">
                        {charCount} characters
                      </div>
                      <div className="flex items-center gap-2">
                        {onRegenerateItem && (
                          <button
                            onClick={() => onRegenerateItem(index)}
                            disabled={regeneratingItem === index}
                            className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white transition-colors"
                            title="Regenerate this tweet"
                          >
                            <RefreshCw size={13} className={regeneratingItem === index ? 'animate-spin' : ''} />
                          </button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(tweet)}
                          className="text-[#00d4aa] hover:bg-[#00d4aa]/10"
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copy
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Copy All Button */}
      <Button
        onClick={copyThread}
        className="w-full bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold h-12"
      >
        {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
        Copy Entire Thread
      </Button>

      {/* Regenerate Section */}
      {onRegenerate && (
        <RegenerateSection
          onRegenerate={onRegenerate}
          isRegenerating={isRegenerating}
          contentType="Twitter"
        />
      )}
    </div>
  );
}

export default TwitterTab;